import { InterpolationContext, useInterpolationCtx } from '@/contexts/InterpolationContext';
import { deepInterpolate } from '@/lib/deepInterpolate';
import { usePage } from '@inertiajs/react';
import { lazy, Suspense, useMemo } from 'react';
import type { PageBlock } from './types';

interface BlockRendererProps {
    block: PageBlock;
    isSelected: boolean;
    selectedElement: { blockId: number; elementPath: string } | null;
    onSelectElement: (elementPath: string) => void;
    isInPageBuilder?: boolean; // True when rendering in Page Builder preview, false on published pages
}

type BoundSourceEntry = {
    alias: string;
    data: unknown;
};

type BoundSourceMap = Record<string, Record<string, BoundSourceEntry>>;

// Block component registry - lazy load for better performance
const blockComponents = {
    composer_section: lazy(() => import('./blocks/ComposerSectionBlock')),
    hero: lazy(() => import('./blocks/HeroBlock')),
    content: lazy(() => import('./blocks/ContentBlock')),
    gallery: lazy(() => import('./blocks/GalleryBlock')),
    cta: lazy(() => import('./blocks/CTABlock')),
    spacer: lazy(() => import('./blocks/SpacerBlock')),
    content_grid: lazy(() => import('./blocks/ContentGridBlock')),
    forbes_endorsement: lazy(() => import('./blocks/ForbesEndorsementBlock')),
    profile_highlight: lazy(() => import('./blocks/ProfileHighlightBlock')),
    image_text: lazy(() => import('./blocks/ImageTextBlock')),
    form: lazy(() => import('./blocks/FormBlock')),
    apply_to_join_our_team: lazy(() => import('./blocks/ApplyToJoinOurTeamBlock')),
    apply_to_join_influencer_program: lazy(() => import('./blocks/ApplyToJoinInfluencerProgramBlock')),
    article_header: lazy(() => import('./blocks/ArticleHeaderBlock')),
    article_lead: lazy(() => import('./blocks/ArticleLeadBlock')),
    person_profile: lazy(() => import('./blocks/PersonProfileBlock')),
    influencer_hero: lazy(() => import('./blocks/InfluencerHeroBlock')),
    story_body: lazy(() => import('./blocks/StoryBodyBlock')),
    seo_metadata: lazy(() => import('./blocks/SeoMetadataBlock').then((module) => ({ default: module.SeoMetadataBlock }))),
    header_config: lazy(() => import('./blocks/HeaderConfigBlock').then((module) => ({ default: module.HeaderConfigBlock }))),
    symposium_hero: lazy(() => import('./blocks/SymposiumHeroBlock')),
    symposium_registration_form: lazy(() => import('./blocks/SymposiumRegistrationFormBlock')),
    tiered_offering: lazy(() => import('./blocks/TieredOfferingBlock')),
    image_with_links_grid: lazy(() => import('./blocks/ImageWithLinksGridBlock')),
    story_with_sidebar: lazy(() => import('./blocks/StoryWithSidebarBlock')),
    headline_with_text: lazy(() => import('./blocks/HeadlineWithTextBlock')),
    showcase_grid: lazy(() => import('./blocks/ShowcaseGridBlock')),
    search: lazy(() => import('./blocks/SearchBlock')),
    image_grid: lazy(() => import('./blocks/ImageGridBlock')),
    logo_grid: lazy(() => import('./blocks/LogoGridBlock')),
    collection_grid: lazy(() => import('./blocks/CollectionGridBlock')),
    black_box_hero: lazy(() => import('./blocks/BlackBoxHeroBlock')),
    feature_hero_single_cta: lazy(() => import('./blocks/FeatureHeroSingleCTABlock')),
    featured_editorial: lazy(() => import('./blocks/FeaturedEditorialBlock')),
    horizontal_collection_grid: lazy(() => import('./blocks/HorizontalCollectionGridBlock')),
    media: lazy(() => import('./blocks/MediaBlock')),
    more_from: lazy(() => import('./blocks/MoreFromBlock')),
    author_bio: lazy(() => import('./blocks/AuthorBioBlock')),
    author_bio_expanded: lazy(() => import('./blocks/AuthorBioExpandedBlock')),
    newsletter_signup: lazy(() => import('./blocks/NewsletterSignupBlock')),
    faq: lazy(() => import('./blocks/FAQBlock')),
    hotel_masthead: lazy(() => import('./blocks/HotelMastheadBlock')),
    hotel_gallery: lazy(() => import('./blocks/HotelGalleryBlock')),
    hotel_overview: lazy(() => import('./blocks/HotelOverviewBlock')),
    hotel_amenities: lazy(() => import('./blocks/HotelAmenitiesBlock')),
    hotel_gds_codes: lazy(() => import('./blocks/HotelGDSCodesBlock')),
    hotel_details: lazy(() => import('./blocks/HotelDetailsBlock')),
    group_client_form: lazy(() => import('./blocks/GroupClientFormBlock')),
    vendor_fastbook_form: lazy(() => import('./blocks/VendorFastBookFormBlock')),
    benefits_grid: lazy(() => import('./blocks/BenefitsGridBlock')),
    benefits_logo_scroller: lazy(() => import('./blocks/BenefitsLogoScrollerBlock')),
    exclusive_access_grid: lazy(() => import('./blocks/ExclusiveAccessGridBlock')),
    editorial_split_hero: lazy(() => import('./blocks/EditorialSplitHeroBlock')),
    editorial_fullbleed_hero: lazy(() => import('./blocks/EditorialFullbleedHeroBlock')),
    app_download_bar: lazy(() => import('./blocks/AppDownloadBarBlock')),
    home_video_feature: lazy(() => import('./blocks/HomeVideoFeatureBlock')),
    founder_quote: lazy(() => import('./blocks/FounderQuoteBlock')),
    journal_cta: lazy(() => import('./blocks/JournalCtaBlock')),
    numbered_steps: lazy(() => import('./blocks/NumberedStepsBlock')),
    split_hero: lazy(() => import('./blocks/SplitHeroBlock')),
    welcome_gateway: lazy(() => import('./blocks/WelcomeGatewayBlock')),
    magazine_hero: lazy(() => import('./blocks/MagazineHeroBlock')),
    editorial_strip: lazy(() => import('./blocks/EditorialStripBlock')),
    category_mosaic: lazy(() => import('./blocks/CategoryMosaicBlock')),
    compact_digest: lazy(() => import('./blocks/CompactDigestBlock')),
    fullbleed_interstitial: lazy(() => import('./blocks/FullbleedInterstitialBlock')),
    editorial_feature: lazy(() => import('./blocks/EditorialFeatureBlock')),
    value_proposition: lazy(() => import('./blocks/ValuePropositionBlock')),
    how_it_works: lazy(() => import('./blocks/HowItWorksBlock')),
    forbes_badge: lazy(() => import('./blocks/ForbesBadgeBlock')),
    expanding_video_hero: lazy(() => import('./blocks/ExpandingVideoHeroBlock')),
    cinema_band: lazy(() => import('./blocks/CinemaBandBlock')),
    journal_sections: lazy(() => import('./blocks/JournalSectionsBlock')),
    journal_preview: lazy(() => import('./blocks/JournalPreviewBlock')),
};

// Fallback component for unknown block types
const UnknownBlock = ({ block }: { block: PageBlock }) => (
    <div className="bg-muted/50 p-8 text-center">
        <p className="text-muted-foreground">
            Unknown block type: <code className="text-sm">{block.block_type}</code>
        </p>
    </div>
);

// Loading component
const BlockLoading = () => (
    <>
        {/* <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div> */}
    </>
);

export function BlockRenderer({ block, isSelected, selectedElement, onSelectElement, isInPageBuilder = false }: BlockRendererProps) {
    const ctx = useInterpolationCtx();
    const { props } = usePage<{ page?: { id: number }; blockSourceBindings?: BoundSourceMap }>();

    const BlockComponent = useMemo(() => {
        const componentKey = block.block_type as keyof typeof blockComponents;
        return blockComponents[componentKey] || UnknownBlock;
    }, [block.block_type]);

    const { effectiveCtx, effectiveRawContent, sourceFieldBindings } = useMemo(() => {
        const blockBindings = props.blockSourceBindings?.[String(block.id)] ?? {};
        if (!blockBindings || typeof blockBindings !== 'object' || Object.keys(blockBindings).length === 0) {
            return {
                effectiveCtx: ctx,
                effectiveRawContent: block.content,
                sourceFieldBindings: {} as Record<string, { alias: string; contextKey: string }>,
            };
        }

        const nextCtx = { ...(ctx as Record<string, unknown>) };
        const nextRawContent = { ...(block.content as Record<string, unknown>) };
        const nextSourceFieldBindings: Record<string, { alias: string; contextKey: string }> = {};

        for (const [sourceField, entry] of Object.entries(blockBindings)) {
            if (!entry || typeof entry !== 'object') continue;
            const alias = typeof entry.alias === 'string' ? entry.alias : '';
            if (!alias) continue;

            const contextKey = `__block_${block.id}_${sourceField}`;
            nextCtx[contextKey] = entry.data;
            nextRawContent[sourceField] = contextKey;
            nextSourceFieldBindings[sourceField] = { alias, contextKey };
        }

        return {
            effectiveCtx: nextCtx,
            effectiveRawContent: nextRawContent,
            sourceFieldBindings: nextSourceFieldBindings,
        };
    }, [ctx, block.content, block.id, props.blockSourceBindings]);

    // In page builder, always preserve undefined templates to show what will be interpolated
    const interpolateOptions = { preserveUndefinedTemplates: true };

    const interpolatedContent = deepInterpolate(effectiveRawContent, effectiveCtx, interpolateOptions);
    const interpolatedSettings = deepInterpolate(block.settings, effectiveCtx, interpolateOptions);

    const blockProps = {
        id: block.id,
        pageId: props.page?.id, // Pass page ID to blocks
        ...interpolatedContent,
        rawContent: effectiveRawContent,
        settings: interpolatedSettings,
        isEditing: isSelected,
        isInPageBuilder, // Pass through from caller - true in PageCanvas, false in PageBuilderView
        selectedElement: selectedElement?.blockId === block.id ? selectedElement.elementPath : null,
        onSelectElement,
        sourceFieldBindings,
    };

    if (BlockComponent === UnknownBlock) {
        return <UnknownBlock block={block} />;
    }

    return (
        <InterpolationContext.Provider value={effectiveCtx as Record<string, unknown>}>
            <Suspense fallback={<BlockLoading />}>
                <BlockComponent {...blockProps} block={block} />
            </Suspense>
        </InterpolationContext.Provider>
    );
}
